import type {
  WalletAddressesMonitoringQuery,
  WalletBalanceSource,
  WalletMonitoringFacets,
  WalletMonitoringToken,
  WalletsMonitoringQuery,
} from '@shared/api/contracts';
import type {
  WalletAddressStatus,
  WalletKind,
  WalletRiskLevel,
  WalletsMonitoringFilters,
} from '../model/types';
import { WALLET_KIND_ORDER } from './wallet-kind';
import { WALLET_TOKEN_ORDER } from './tokens';

/** Backend caps wallets/addresses monitoring lists at 100 items per request. */
export const WALLET_MONITORING_FETCH_LIMIT = 100;

export const WALLET_BALANCE_SOURCES: WalletBalanceSource[] = ['provider', 'addresses', 'ledger_only'];
export const WALLET_RISK_OPTIONS: WalletRiskLevel[] = ['low', 'medium', 'high', 'critical', 'unknown'];
export const WALLET_STATUS_OPTIONS: WalletAddressStatus[] = [
  'active',
  'disabled',
  'maintenance',
  'blocked',
  'receive_only',
  'withdraw_only',
  'archived',
];

export const DEFAULT_WALLET_FILTERS: WalletsMonitoringFilters = {
  search: '',
  dateFrom: '',
  dateTo: '',
  groupCodes: [],
  walletKinds: [],
  balanceSources: [],
  networkCodes: [],
  currencyCodes: [],
  tokens: [],
  riskLevels: [],
  statuses: [],
  amlMonitoring: null,
  sort: 'createdAt',
  order: 'desc',
};

export interface WalletFilterUniverses {
  groupCodes?: string[];
  networkCodes?: string[];
  currencyCodes?: string[];
}

/** Empty or fully-selected chips omit the param; a subset sends only selected values. */
export const isFilterListNarrowed = <T>(selected: T[], universe: readonly T[]): boolean =>
  selected.length > 0 &&
  (universe.length === 0 ||
    selected.length !== universe.length ||
    universe.some((item) => !selected.includes(item)));

const resolveFilterParam = <T>(selected: T[], universe: readonly T[]): T[] | undefined => {
  if (isFilterListNarrowed(selected, universe)) {
    return selected;
  }

  return undefined;
};

const toggleInList = <T>(list: T[], value: T): T[] => {
  if (list.includes(value)) {
    return list.filter((item) => item !== value);
  }
  return [...list, value];
};

export const toggleGroupCode = (codes: string[], code: string) => toggleInList(codes, code);
export const toggleKind = (kinds: WalletKind[], kind: WalletKind) => toggleInList(kinds, kind);
export const toggleBalanceSource = (sources: WalletBalanceSource[], source: WalletBalanceSource) =>
  toggleInList(sources, source);
export const toggleNetwork = (networks: string[], network: string) =>
  toggleInList(networks, network);
export const toggleCurrency = (currencies: string[], currency: string) =>
  toggleInList(currencies, currency);
export const toggleToken = (tokens: WalletMonitoringToken[], token: WalletMonitoringToken) =>
  toggleInList(tokens, token);
export const toggleRisk = (risks: WalletRiskLevel[], risk: WalletRiskLevel) =>
  toggleInList(risks, risk);
export const toggleStatus = (statuses: WalletAddressStatus[], status: WalletAddressStatus) =>
  toggleInList(statuses, status);

export const toWalletMonitoringQuery = (
  filters: WalletsMonitoringFilters,
  page: number,
  limit: number,
  universes: WalletFilterUniverses = {},
): WalletsMonitoringQuery => {
  const search = filters.search.trim();
  const groupCodes = resolveFilterParam(filters.groupCodes, universes.groupCodes ?? []);
  const walletKinds = resolveFilterParam(filters.walletKinds, WALLET_KIND_ORDER);
  const balanceSources = resolveFilterParam(filters.balanceSources, WALLET_BALANCE_SOURCES);
  const networkCodes = resolveFilterParam(filters.networkCodes, universes.networkCodes ?? []);
  const currencyCodes = resolveFilterParam(filters.currencyCodes, universes.currencyCodes ?? []);
  const tokens = resolveFilterParam(filters.tokens, WALLET_TOKEN_ORDER);
  const riskLevels = resolveFilterParam(filters.riskLevels, WALLET_RISK_OPTIONS);
  const statuses = resolveFilterParam(filters.statuses, WALLET_STATUS_OPTIONS);

  return {
    page,
    limit,
    ...(search ? { search } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    ...(groupCodes ? { groupCodes } : {}),
    ...(walletKinds ? { walletKinds } : {}),
    ...(balanceSources ? { balanceSources } : {}),
    ...(networkCodes ? { networkCodes } : {}),
    ...(currencyCodes ? { currencyCodes } : {}),
    ...(tokens ? { tokens } : {}),
    ...(riskLevels ? { riskLevels } : {}),
    ...(statuses ? { statuses } : {}),
    ...(filters.amlMonitoring !== null ? { amlMonitoring: filters.amlMonitoring } : {}),
    sort: filters.sort,
    order: filters.order,
  };
};

export const toWalletAddressQuery = (
  filters: WalletsMonitoringFilters,
  page: number,
  limit: number,
  universes: WalletFilterUniverses = {},
): WalletAddressesMonitoringQuery => {
  const search = filters.search.trim();
  const networkCodes = resolveFilterParam(filters.networkCodes, universes.networkCodes ?? []);
  const currencyCodes = resolveFilterParam(filters.currencyCodes, universes.currencyCodes ?? []);
  const tokens = resolveFilterParam(filters.tokens, WALLET_TOKEN_ORDER);
  const riskLevels = resolveFilterParam(filters.riskLevels, WALLET_RISK_OPTIONS);
  const statuses = resolveFilterParam(filters.statuses, WALLET_STATUS_OPTIONS);

  return {
    page,
    limit,
    ...(search ? { search } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    ...(networkCodes ? { networkCodes } : {}),
    ...(currencyCodes ? { currencyCodes } : {}),
    ...(tokens ? { tokens } : {}),
    ...(riskLevels ? { riskLevels } : {}),
    ...(statuses ? { statuses } : {}),
    ...(filters.amlMonitoring !== null ? { amlMonitoring: filters.amlMonitoring } : {}),
  };
};

export const hasAddressFilters = (
  filters: WalletsMonitoringFilters,
  universes: WalletFilterUniverses = {},
): boolean =>
  Boolean(
    filters.search.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      isFilterListNarrowed(filters.networkCodes, universes.networkCodes ?? []) ||
      isFilterListNarrowed(filters.currencyCodes, universes.currencyCodes ?? []) ||
      isFilterListNarrowed(filters.tokens, WALLET_TOKEN_ORDER) ||
      isFilterListNarrowed(filters.riskLevels, WALLET_RISK_OPTIONS) ||
      isFilterListNarrowed(filters.statuses, WALLET_STATUS_OPTIONS) ||
      filters.amlMonitoring !== null,
  );

export interface WalletFilterCatalog {
  networkCodes: string[];
  currencyCodes: string[];
}

export const toWalletFilterCatalog = (
  facets?: WalletMonitoringFacets,
): WalletFilterCatalog => ({
  networkCodes: facets?.networks.map((item) => item.value) ?? [],
  currencyCodes: facets?.currencies.map((item) => item.value) ?? [],
});

export const mergeStringOptions = (catalog: string[], selected: string[]): string[] => {
  const values = new Set([...catalog, ...selected]);
  return [...values].sort((left, right) => left.localeCompare(right));
};
